import { v4 as uuid } from 'uuid';

import type { State } from '@framework/appInsights';
import { decamelizeKeys } from '@framework/utils/camelize.utils';

import type { Member } from '@type/members';
import { PERMISSION_SETS } from '@type/permissions';

/**
 * Key used to identify current session ( used to identify events coming from the same tab )
 */
export const appInsightsSessionKey = uuid();

export const getInsightPayload = (state?: State, data = {}) => {
    if (!state) {
        return decamelizeKeys({
            stack: 'react',
            url: window.location.href,
            appSessionId: appInsightsSessionKey
        });
    }
    const { isAttendeeView } = state;

    return decamelizeKeys({
        stack: 'react',
        appSessionId: appInsightsSessionKey,
        view: isAttendeeView ? 'member' : 'secretary',
        ...data
    });
};

export const getURLWithMaskedRoomName = () => {
    if (typeof window === 'undefined') {
        return '';
    }
    const matchResult = window?.location.pathname.match(/\/rooms\/([^/]+)\//);
    const roomName = matchResult?.[1] ?? '';

    return window.location.href.replace(roomName, 'room');
};

const USER_PERMISSION_INSIGHTS_LABEL: Record<PERMISSION_SETS, string> = {
    [PERMISSION_SETS.MEMBER]: 'Member',
    [PERMISSION_SETS.EDITOR]: 'Editor',
    [PERMISSION_SETS.GROUP_MANAGER]: 'Editor / Group manager',
    [PERMISSION_SETS.USER_MANAGER]: 'User management admin',
    [PERMISSION_SETS.OLD_ASSISTANT]: 'Assistant',
    [PERMISSION_SETS.ASSISTANT]: 'Assistant',
    [PERMISSION_SETS.ADMIN]: 'Admin',
    [PERMISSION_SETS.CONTRIBUTOR]: 'Contributor',

    [PERMISSION_SETS.RESTRICTED_MEMBER]: 'User with restricted permissions',
    [PERMISSION_SETS.DEFAULT_MEMBER]: 'User',
    [PERMISSION_SETS.GUEST]: 'Guest license',
    [PERMISSION_SETS.MINUTES_VIEWER]: 'Minutes viewer',

    [PERMISSION_SETS.DEFAULT_MEMBER_CAN_MANAGE_GROUPS_I_AM_MANAGER_OF]: 'User + Group management',
    [PERMISSION_SETS.DEFAULT_MEMBER_CAN_MANAGE_ALL_MEMBERS_AND_GROUPS]: 'User + User management',
    [PERMISSION_SETS.DEFAULT_MEMBER_CAN_MANAGE_ALL_MEMBERS_ALL_GROUPS_ALL_CONTENT]:
        'User + User management + Access to all content',

    [PERMISSION_SETS.DOWNLOAD_DOCUMENTS_FROM_NATIVE_APPS]: 'Download documents from native apps'
};

const USER_BASE_PERMISSION_PRIORITY: PERMISSION_SETS[] = [
    PERMISSION_SETS.DEFAULT_MEMBER_CAN_MANAGE_ALL_MEMBERS_ALL_GROUPS_ALL_CONTENT,
    PERMISSION_SETS.DEFAULT_MEMBER_CAN_MANAGE_ALL_MEMBERS_AND_GROUPS,
    PERMISSION_SETS.DEFAULT_MEMBER_CAN_MANAGE_GROUPS_I_AM_MANAGER_OF,
    PERMISSION_SETS.DEFAULT_MEMBER,

    PERMISSION_SETS.RESTRICTED_MEMBER,
    PERMISSION_SETS.GUEST,
    PERMISSION_SETS.MINUTES_VIEWER,
    PERMISSION_SETS.CONTRIBUTOR,

    PERMISSION_SETS.ASSISTANT,
    PERMISSION_SETS.OLD_ASSISTANT,

    PERMISSION_SETS.ADMIN,
    PERMISSION_SETS.GROUP_MANAGER,
    PERMISSION_SETS.USER_MANAGER,
    PERMISSION_SETS.EDITOR,
    PERMISSION_SETS.MEMBER
];

export const getCurrentUserPermissionsInsights = (member?: Member): string | undefined => {
    if (!member || !member.permissionSets?.length) return undefined;

    const { permissionSets } = member;

    const permissionSet = USER_BASE_PERMISSION_PRIORITY.find((s) => permissionSets.includes(s));
    if (!permissionSet) return undefined;

    return USER_PERMISSION_INSIGHTS_LABEL[permissionSet];
};
